import React, { useState, useEffect, useCallback } from 'react';
import { Bell, AlertCircle, Package, AlertTriangle, MessageSquare, Check, Trash2, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { getNotifications, markNotificationRead, markAllNotificationsRead, clearNotifications } from '../services/api';
import Loader from './Loader';
import { toast } from 'sonner';

const TYPE_CONFIG = {
  reminder: { icon: AlertCircle, color: 'bg-red-100 text-red-500' },
  upcoming: { icon: Clock, color: 'bg-orange-100 text-orange-500' },
  stock_alert: { icon: Package, color: 'bg-red-100 text-red-500' },
  expiry_alert: { icon: AlertTriangle, color: 'bg-amber-100 text-amber-500' },
  custom: { icon: MessageSquare, color: 'bg-blue-100 text-blue-500' },
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Az önce';
  if (diffMins < 60) return `${diffMins} dk önce`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} saat önce`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} gün önce`;
  return d.toLocaleDateString('tr-TR');
};

const NotificationsModal = ({ open, onClose, onCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await getNotifications();
      setNotifications(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) { setLoading(true); loadNotifications(); }
  }, [open, loadNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (onCountChange) onCountChange(unreadCount);
  }, [unreadCount, onCountChange]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch { toast.error('Hata oluştu'); }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Tümü okundu olarak işaretlendi');
    } catch { toast.error('Hata oluştu'); }
  };

  const handleClearAll = async () => {
    try {
      await clearNotifications();
      setNotifications([]);
      toast.success('Bildirimler temizlendi');
    } catch { toast.error('Hata oluştu'); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent data-testid="notifications-modal" className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-4 lg:px-6 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <DialogTitle className="flex items-center justify-between pr-8">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center shrink-0">
                <Bell size={18} className="text-red-500" />
              </div>
              <div>
                <h2 className="text-base lg:text-lg font-bold text-gray-900">Bildirimler</h2>
                <span className="block text-xs text-[#8E8E93] font-normal">{notifications.length} bildirim{unreadCount > 0 ? ` · ${unreadCount} okunmamış` : ''}</span>
              </div>
            </div>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-[#6b6de3] text-xs shrink-0" data-testid="mark-all-read-btn">
                <Check size={14} className="mr-1" /> Tümünü Oku
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader /></div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell size={40} className="text-[#C7C7CC] mx-auto mb-2" />
              <p className="text-sm text-[#8E8E93]">Bildirim yok</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {notifications.map(n => {
                const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.custom;
                const Icon = cfg.icon;
                return (
                  <div
                    key={n.id}
                    onClick={() => !n.read && handleMarkRead(n.id)}
                    className={`px-4 lg:px-6 py-3 flex items-start gap-3 cursor-pointer transition-colors hover:bg-gray-50 ${!n.read ? 'bg-blue-50/30' : 'opacity-70'}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cfg.color}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm font-semibold truncate ${!n.read ? 'text-gray-900' : 'text-gray-500'}`}>
                          {n.title}
                        </p>
                        {!n.read && <div className="w-2 h-2 rounded-full bg-[#6b6de3] shrink-0" />}
                      </div>
                      <p className="text-xs text-[#8E8E93] mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-[#C7C7CC] mt-1">{formatTime(n.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="p-4 lg:p-6 border-t border-gray-100 shrink-0">
            <Button
              variant="outline"
              onClick={handleClearAll}
              className="w-full rounded-full text-[#FF3B30] border-[#FF3B30]/30 hover:bg-red-50"
              data-testid="clear-all-btn"
            >
              <Trash2 size={16} className="mr-2" /> Bildirimleri Temizle
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationsModal;
